import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AuthButton from "@/components/AuthButton";
import "./LandingPage.css";

/**
 * Landing page.
 *
 * Deliberately makes ZERO network calls. The backend runs scale-to-zero on Fly,
 * so anything fetched here would wake a suspended machine and stall first paint
 * by several seconds. The API is only touched once someone reaches /app.
 */

function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const nodes = ref.current?.querySelectorAll(".lp-reveal");
    if (!nodes?.length) return;

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      nodes.forEach((n) => n.classList.add("is-in"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px" }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  return ref;
}

/** Nav goes solid once the hero is behind you. */
function useStuck() {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return stuck;
}

const STEPS = [
  {
    n: "01",
    title: "Your moment and place",
    body:
      "The exact minute and the coordinates you were born at, resolved to the timezone actually in force there that year — including the historical daylight-saving rules most tools get wrong.",
  },
  {
    n: "02",
    title: "The zenith point",
    body:
      "From that moment, the local sidereal time gives the one point on the celestial sphere that sat precisely above you. Everything else follows from it.",
  },
  {
    n: "03",
    title: "The catalog, rewound",
    body:
      "Gaia DR3 is queried for the corridor around that point, then every star is rewound along its own proper motion to where it actually stood in your birth year.",
  },
];

/* 0.2° at a 600 mm arm's length subtends 600 × tan(0.2°) = 2.09 mm. */
const SCALE = [
  ["2 mm", "The width of that corridor, held at arm's length. About a pinhead."],
  ["12′", "Twelve arcminutes. Two-fifths the width of a full moon."],
  ["1.8 billion", "Stars Gaia has measured. Around 6,000 are visible to the naked eye."],
];

const TIERS = [
  {
    name: "Digital",
    price: "$29.90",
    unit: "one-time",
    features: [
      "Print-resolution image, 300 DPI",
      "Star ledger PDF — every star, named and catalogued",
      "CSV of the full dataset",
      "Yours to print anywhere, forever",
    ],
    cta: "Start with your date",
    featured: true,
  },
  {
    name: "Fine art print",
    price: "$89",
    unit: "12 × 36 in · unframed",
    features: [
      "Giclée on archival cotton rag",
      "Panoramic format, cut to the ribbon",
      "Printed and shipped in the USA",
      "Digital files included",
    ],
    cta: "Start with your date",
  },
  {
    name: "Framed",
    price: "$149",
    unit: "12 × 36 in · framed",
    features: [
      "Everything in Fine art print",
      "Solid ash frame, museum glass",
      "Engraved plate: date, time, coordinates",
      "Arrives ready to hang",
    ],
    cta: "Start with your date",
  },
];

/* Real hips2fits frames at the TRUE ribbon geometry: fov applies to width, so
   1400x280 at fov=1.0 is exactly 1.0° x 0.2° — the corridor the product returns.
   Deliberately NOT wide-field nebula portraits: those would advertise something
   the 0.2° window does not deliver. */
const PLATES = [
  ["nebula", "Sagittarius, on the galactic plane", "RA 271.00° Dec \u221224.00° · 1.0° \u00d7 0.2°"],
  ["dense", "Cygnus, dust and dense field", "RA 307.20° Dec +40.60° · 1.0° \u00d7 0.2°"],
  ["cluster", "The edge of the Pleiades", "RA 56.90° Dec +24.30° · 1.0° \u00d7 0.2°"],
  ["sparse", "High galactic latitude", "RA 190.00° Dec +30.00° · 1.0° \u00d7 0.2°"],
];

/* Same patch of sky, three magnifications. */
const ZOOM = [
  ["60", "60° — naked-eye view"],
  ["6", "6° — a pair of binoculars"],
  ["10", "1° — your band"],
];

const SPEC = [
  ["Measurements", "Gaia DR3, queried live via the Potsdam TAP service"],
  ["Star names", "AT-HYG registry, matched on HIP / HD / HR / TYC identifiers"],
  ["Corridor width", "0.2° — twelve arcminutes"],
  ["Epoch correction", "J2016.0 rewound to your birth year, per star"],
  ["Constellations", "All 88 IAU boundaries, B1875.0"],
  ["Typical result", "2,700–4,800 stars"],
];

export default function LandingPage() {
  const ref = useReveal();
  const stuck = useStuck();

  return (
    <div className="lp" ref={ref}>
      <a className="lp-skip" href="#sky">Skip to content</a>

      {/* ----------------------------------------------------------------- NAV */}
      <nav className={`lp-nav${stuck ? " is-stuck" : ""}`}>
        <a className="lp-nav-mark" href="#top">Stars at Birth</a>
        <div className="lp-nav-links">
          <a href="#sky">The sky</a>
          <a href="#scale">Scale</a>
          <a href="#gallery">Plates</a>
          <a href="#unique">Uniqueness</a>
          <a href="#get">What you get</a>
          <Link to="/faq">FAQ</Link>
        </div>
        <div className="lp-nav-right">
          <AuthButton />
          <Link className="lp-btn lp-btn-primary lp-nav-cta" to="/app">Try it</Link>
        </div>
      </nav>

      {/* ---------------------------------------------------------------- HERO */}
      <section className="lp-hero" id="top">
        <video
          className="lp-hero-video"
          src="/media/zenith-hero.mp4"
          poster="/media/zenith-hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          tabIndex={-1}
        />
        <div className="lp-hero-scrim" />

        <div className="lp-hero-inner">
          <span className="lp-mono lp-eyebrow">
            Gaia DR3 · 0.2° corridor · rewound to your epoch
          </span>

          <div className="lp-ribbon">
            <span className="lp-mono lp-ribbon-label">0.2°</span>
            <h1 className="lp-display lp-h1">
              The sky <em>directly overhead</em>
            </h1>
          </div>

          <p className="lp-body lp-lede">
            At the minute you were born, one narrow band of stars stood exactly above the
            spot you were born on. This finds them, by name, from the real catalog.
          </p>

          <div className="lp-actions">
            <Link className="lp-btn lp-btn-primary" to="/app">Try it</Link>
            <a className="lp-btn lp-btn-ghost" href="#sky">How it works</a>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------- THE SKY */}
      <section className="lp-section" id="sky">
        <div className="lp-section-head lp-reveal">
          <span className="lp-mono">The sky</span>
          <h2 className="lp-display lp-h2">Straight up is the only direction that is yours</h2>
        </div>

        <p className="lp-body lp-prose lp-reveal">
          Astrology looks at planets near the horizon, shared across whole continents. Anyone
          born the same week, almost anywhere, gets broadly the same reading. That is the
          opposite of personal.
        </p>
        <p className="lp-body lp-prose lp-reveal">
          The <strong>zenith</strong> is different. It is the single direction that points
          straight up from one exact spot on Earth — a line drawn from the centre of the
          planet, through the place you were born, out into space. Move a few miles, or wait
          four minutes, and it points somewhere else entirely.
        </p>
        <p className="lp-body lp-prose lp-reveal">
          Think of it as a laser fired upward from the hospital floor at the moment you
          arrived. It punches a narrow cylinder through the atmosphere and keeps going. What
          this does is read back the stars that cylinder passed through — the closest thing
          there is to a sky that belongs to one person and one moment.
        </p>
        <p className="lp-body lp-prose lp-reveal">
          Nothing here is interpreted, and nothing is invented. It is the observation only.
          What it means is left to you.
        </p>
      </section>

      {/* --------------------------------------------------------------- SCALE */}
      <section className="lp-section" id="scale">
        <div className="lp-section-head lp-reveal">
          <span className="lp-mono">Scale</span>
          <h2 className="lp-display lp-h2">How narrow 0.2° really is</h2>
        </div>

        <p className="lp-body lp-prose lp-reveal">
          Hold a pin at arm&rsquo;s length. The head of it covers roughly the same slice of sky
          this measures — about two millimetres at sixty centimetres. Point that at the night
          sky and you would swear it was empty.
        </p>

        <div className="lp-scale">
          {SCALE.map(([fig, cap]) => (
            <div className="lp-scale-item lp-reveal" key={fig}>
              <div className="lp-scale-fig">{fig}</div>
              <p className="lp-body lp-scale-cap">{cap}</p>
            </div>
          ))}
        </div>

        <p className="lp-body lp-prose lp-reveal" style={{ marginTop: "3rem" }}>
          It is not empty. On a good night you might see six thousand stars in the entire
          sky. Gaia has measured nearly <strong>two billion</strong>. Almost everything above
          you is too faint for human eyes and always has been — so this zooms in to a patch
          you could hide behind a pinhead, and still finds thousands of stars waiting in it.
        </p>

        <div className="lp-zoom lp-reveal">
          {ZOOM.map(([n, cap], i) => (
            <figure key={n}>
              <img
                className={i === ZOOM.length - 1 ? "is-target" : undefined}
                src={`/media/zoom-${n}.jpg`}
                alt={cap}
                loading="lazy"
                width={900}
                height={600}
              />
              <figcaption>{cap}</figcaption>
            </figure>
          ))}
        </div>
        <p className="lp-note lp-reveal">
          The same patch of sky in Sagittarius, three times. Digitized Sky Survey 2, via CDS.
        </p>
      </section>

      {/* ------------------------------------------------------------ GALLERY */}
      <section className="lp-section" id="gallery">
        <div className="lp-section-head lp-reveal">
          <span className="lp-mono">Plates</span>
          <h2 className="lp-display lp-h2">What a strip of sky looks like</h2>
        </div>

        <p className="lp-body lp-prose lp-reveal">
          These are not illustrations, and they are not wide-field nebula portraits either.
          Each one is a real survey frame at <strong>exactly the size your band comes in</strong>
          {" "}— one degree across, two-tenths of a degree tall.
        </p>
        <p className="lp-body lp-prose lp-reveal" style={{ marginBottom: "2.75rem" }}>
          How much lands in yours depends entirely on where your zenith was pointing. Aim
          through the plane of the galaxy and it is crowded. Aim out of it and you get a
          quiet, near-empty field. Both are real results, and the last one below is what
          that looks like.
        </p>

        <div className="lp-gallery">
          {PLATES.map(([slug, name, coords]) => (
            <figure className="lp-plate lp-reveal" key={slug} style={{ margin: 0 }}>
              <img
                src={`/media/band-${slug}.jpg`}
                alt={`${name} — a real 1.0 by 0.2 degree survey frame`}
                loading="lazy"
                width={1400}
                height={280}
              />
              <figcaption className="lp-plate-meta">
                <h3 className="lp-plate-name">{name}</h3>
                <span className="lp-plate-coords">{coords}</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="lp-note lp-reveal">
          Digitized Sky Survey 2 (colour), via CDS hips2fits.
        </p>
      </section>

      {/* ------------------------------------------------------------------ HOW */}
      <section className="lp-section" id="how">
        <div className="lp-section-head lp-reveal">
          <span className="lp-mono">How it works</span>
          <h2 className="lp-display lp-h2">Three steps, in order</h2>
        </div>

        <ol className="lp-steps">
          {STEPS.map((s) => (
            <li className="lp-step lp-reveal" key={s.n}>
              <span className="lp-mono lp-step-n">{s.n}</span>
              <h3 className="lp-step-t">{s.title}</h3>
              <p className="lp-body lp-step-b">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ------------------------------------------------------------- UNIQUE */}
      <section className="lp-section" id="unique">
        <div className="lp-section-head lp-reveal">
          <span className="lp-mono">Uniqueness</span>
          <h2 className="lp-display lp-h2">Why yours cannot be repeated</h2>
        </div>

        <p className="lp-body lp-prose lp-reveal">
          The honest version, with the arithmetic done. The whole sky measures about 41,000
          square degrees. A band this size covers roughly a tenth of one. So the sky holds
          somewhere around <strong>a quarter of a million</strong> patches like yours — a lot,
          but a long way short of infinite. Given enough births, someone will overlap you.
          That is arithmetic, not marketing, and we are not going to pretend otherwise.
        </p>
        <p className="lp-body lp-prose lp-reveal">
          It does not matter, because a patch of sky is not a flat picture. Every star in it
          sits at a wildly different distance — one thirty light years away, its neighbour on
          the print eight hundred. They are not near each other at all. They only appear to
          be, from one specific vantage point, for a moment.
        </p>
        <p className="lp-body lp-prose lp-reveal">
          And they are all moving. Not visibly — the fastest-moving star in the sky needs about
          two centuries to shift by the width of a full moon — but measurably, each on its own
          path at its own speed. That is why the catalogue has to be rewound: the pattern in
          your band is not the pattern there today, and it will not be the pattern in a
          hundred years. <strong>The same patch of sky is never the same twice.</strong>
        </p>
        <p className="lp-body lp-prose lp-reveal">
          So the patch is not what makes it yours. The arrangement is — those particular stars,
          at those particular distances, in those particular positions, seen from one spot on
          Earth at one minute that will not recur.
        </p>

        <p className="lp-pull lp-reveal">
          Two people could share the patch. Nobody shares the moment it was seen from.
        </p>
      </section>

      {/* ---------------------------------------------------------------- GET */}
      <section className="lp-section" id="get">
        <div className="lp-section-head lp-reveal">
          <span className="lp-mono">What you get</span>
          <h2 className="lp-display lp-h2">Take it home</h2>
        </div>

        <p className="lp-body lp-prose lp-reveal" style={{ marginBottom: "2.75rem" }}>
          Calculating your band is free — put in a date and place and look at it as long as
          you like. You only pay if you want to keep it.
        </p>

        <div className="lp-mockups lp-reveal">
          <figure className="lp-mockup">
            <img src="/media/mockup-framed.jpg" alt="Framed print with a cream passe-partout in a black frame"
                 loading="lazy" width={1600} height={679} />
            <figcaption>Framed · passe-partout mount · black frame</figcaption>
          </figure>
          <figure className="lp-mockup">
            <img src="/media/mockup-canvas.jpg" alt="Gallery-wrapped canvas showing the image continuing around the edge"
                 loading="lazy" width={1600} height={679} />
            <figcaption>Gallery-wrapped canvas</figcaption>
          </figure>
        </div>

        <div className="lp-tiers">
          {TIERS.map((t) => (
            <div
              className={`lp-tier lp-reveal${t.featured ? " lp-tier-featured" : ""}`}
              key={t.name}
            >
              <h3 className="lp-tier-name">{t.name}</h3>
              <div className="lp-tier-price">{t.price}</div>
              <div className="lp-tier-unit">{t.unit}</div>
              <ul>
                {t.features.map((f) => <li key={f}>{f}</li>)}
              </ul>
              <div className="lp-tier-foot">
                <Link className="lp-btn lp-btn-ghost" to="/app">{t.cta}</Link>
              </div>
            </div>
          ))}
        </div>

        <p className="lp-note lp-reveal">
          Print sizes, framing and pricing are indicative while fulfilment is being finalised
          with US print partners. Digital delivery is immediate.
        </p>
      </section>

      {/* --------------------------------------------------------------- SPEC */}
      <section className="lp-section" id="data">
        <div className="lp-section-head lp-reveal">
          <span className="lp-mono">The data</span>
          <h2 className="lp-display lp-h2">What sits underneath</h2>
        </div>

        <table className="lp-spec lp-reveal">
          <tbody>
            {SPEC.map(([k, v]) => (
              <tr key={k}>
                <th scope="row">{k}</th>
                <td>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* -------------------------------------------------------------- CLOSE */}
      <section className="lp-close">
        <div className="lp-reveal">
          <h2 className="lp-display lp-h2">Find your band</h2>
          <p className="lp-body lp-prose">
            You need a date, a time, and a place. The rest is arithmetic.
          </p>
          <div className="lp-actions">
            <Link className="lp-btn lp-btn-primary" to="/app">Try it</Link>
          </div>
        </div>
      </section>

      <footer className="lp-foot">
        <Link to="/faq" style={{ color: '#67e8f9', textDecoration: 'none' }}>Questions &amp; answers</Link>
        <br /><br />
        Measurements from Gaia DR3 · ESA/Gaia/DPAC · names from the AT-HYG registry ·
        constellation boundaries after Delporte (1930)
      </footer>
    </div>
  );
}
